import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getGameStatus } from '@/lib/chess-engine';
import { createInitialGameState, GameState, Position, validateMove, makeMove } from '@/lib/chess-engine';
import { useToast } from '@/hooks/use-toast';
import { parseTimeControl } from '@/lib/clock-utils';
import { useAuthResilience } from '@/hooks/useAuthResilience';
import { normalizeGameState } from '@/lib/gameStateUtils';

interface Room {
  id: string;
  room_code: string;
  status: string;
  time_control: string;
  created_by: string;
  room_name?: string;
}

interface GameSession {
  id: string;
  room_id: string;
  game_state: any; // JSON from database
  current_turn: string; // Allow string from database
  white_player_id: string | null;
  black_player_id: string | null;
  game_status: string;
  move_count: number;
  white_time_ms: number | null;
  black_time_ms: number | null;
  increment_ms: number | null;
  last_tick_at: string | null;
  clocks_paused: boolean | null;
}

interface RoomParticipant {
  id: string;
  room_id: string;
  user_id: string;
  player_color: string | null; // Allow string from database
  is_connected: boolean;
}

export function useMultiplayerGame() {
  const [room, setRoom] = useState<Room | null>(null);
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [participants, setParticipants] = useState<RoomParticipant[]>([]);
  const [playerColor, setPlayerColor] = useState<'white' | 'black' | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [channel, setChannel] = useState<any>(null);
  const { toast } = useToast();
  const { withAuthRetry, handleAuthError, isRefreshing } = useAuthResilience();

  // Track mount status to guard async state updates
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const createRoom = useCallback(async (roomCode: string, timeControl: string) => {
    return withAuthRetry(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('authentication_required');
      }

      const normalizedCode = roomCode.trim().toUpperCase();
      console.log('Creating room with code:', normalizedCode);

      // Create room using RPC - creator should be automatically added as participant
      const { data: roomData, error: roomError } = await supabase
        .rpc('create_temp_room', {
          _room_code: normalizedCode,
          _time_control: timeControl,
          _is_public: true,
          _max_participants: 2
        });

      console.log('RPC Response:', { roomData, roomError });

      if (roomError) {
        console.error('Room creation failed:', roomError);
        
        // Let useAuthResilience handle auth errors, handle other errors here
        if (roomError.message?.toLowerCase().includes('authentication_required') || 
            roomError.message?.toLowerCase().includes('not authenticated')) {
          throw new Error('authentication_required');
        } else if (roomError.message?.includes('room_creation_failed')) {
          toast({
            title: "Room Creation Failed", 
            description: "Unable to create room. The room code might already exist or there was a server error.",
            variant: "destructive"
          });
          throw roomError;
        } else {
          toast({
            title: "Room Creation Failed",
            description: roomError.message || "Failed to create room. Please try again.",
            variant: "destructive"
          });
          throw roomError;
        }
      }

      if (!roomData || roomData.length === 0) {
        console.error('No room data returned from RPC');
        toast({
          title: "Room Creation Failed", 
          description: "No room data returned from server. Please try again.",
          variant: "destructive"
        });
        return false;
      }

      const createdRoom = roomData[0];
      console.log('Room created successfully:', createdRoom);

      // Verify room was created and creator was added as participant
      const { data: participants, error: participantsError } = await supabase
        .from('room_participants')
        .select('*')
        .eq('room_id', createdRoom.id);

      if (participantsError) {
        console.error('Error fetching participants after room creation:', participantsError);
        toast({
          title: "Room Creation Warning",
          description: "Room created but failed to verify participants. Please try joining manually.",
          variant: "destructive"
        });
        return false;
      }

      console.log('Participants after room creation:', participants);

      // Check if creator was properly added as participant by the RPC function
      const creatorParticipant = participants?.find(p => p.user_id === user.id);
      if (!creatorParticipant) {
        console.warn('Creator not found in participants after RPC, adding manually...');
        
        const { error: addParticipantError } = await supabase
          .from('room_participants')
          .insert({
            room_id: createdRoom.id,
            user_id: user.id,
            player_color: 'white',
            is_connected: true,
            joined_at: new Date().toISOString()
          });

        if (addParticipantError) {
          console.error('Failed to add creator as participant:', addParticipantError);
          toast({
            title: "Room Creation Error",
            description: "Room created but you cannot join it. Please try creating a different room code.",
            variant: "destructive"
          });
          return false;
        }

        console.log('Creator added manually as participant');
        
        // Re-fetch participants after manual addition
        const { data: updatedParticipants } = await supabase
          .from('room_participants')
          .select('*')
          .eq('room_id', createdRoom.id);
        
        setParticipants(updatedParticipants || []);
      } else {
        console.log('Creator successfully found as participant');
        setParticipants(participants || []);
      }

      // Set room state
      setRoom({
        ...createdRoom,
        status: 'waiting',
        created_by: user.id
      });
      setPlayerColor('white');
      
      toast({
        title: "Room Created",
        description: `Room ${normalizedCode} created successfully. Share the code with your opponent!`,
      });
      
      return true;
      
    }, 'room creation').catch((error) => {
      // Auth errors are already handled by useAuthResilience
      if (error.message !== 'authentication_required') {
        toast({
          title: "Room Creation Failed",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive"
        });
      }
      return false;
    });
  }, [toast, withAuthRetry]);

  const joinRoom = useCallback(async (roomCode: string) => {
    return withAuthRetry(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('authentication_required');
      }

      const normalizedCode = roomCode.trim().toUpperCase();
      console.log('Attempting to join room:', normalizedCode);

      // Check if user is already in a room with this code (handles self-joins)
      const { data: existingRoomData } = await supabase
        .from('rooms')
        .select('id, room_code, time_control, created_by')
        .eq('room_code', normalizedCode)
        .maybeSingle();

      if (existingRoomData) {
        const { data: existingParticipation } = await supabase
          .from('room_participants')
          .select('*')
          .eq('room_id', existingRoomData.id)
          .eq('user_id', user.id)
          .maybeSingle();

        if (existingParticipation) {
          console.log('User already in this room, updating state...');
          
          // Set room state
          setRoom({
            id: existingRoomData.id,
            room_code: existingRoomData.room_code,
            time_control: existingRoomData.time_control,
            status: 'waiting',
            created_by: existingRoomData.created_by
          });

          // Fetch all participants and set state
          const { data: allParticipants } = await supabase
            .from('room_participants')
            .select('*')
            .eq('room_id', existingRoomData.id);

          setParticipants(allParticipants || []);
          setPlayerColor(existingParticipation.player_color as 'white' | 'black');

          // Check for existing game session
          const { data: existingSession } = await supabase
            .from('game_sessions')
            .select('*')
            .eq('room_id', existingRoomData.id)
            .maybeSingle();

          if (existingSession) {
            setGameSession(existingSession);
          }

          toast({
            title: "Rejoined Room",
            description: `You're back in room ${normalizedCode}`,
          });

          return true;
        }
      }

      // Use the RPC function to join room and add as participant
      const { data: roomData, error: roomError } = await supabase.rpc('join_room_by_code', {
        _room_code: normalizedCode
      });

      console.log('RPC response:', { roomData, roomError });

      if (roomError) {
        console.error('Error joining room:', roomError);
        
        // Let useAuthResilience handle auth errors, handle other errors here
        if (roomError.message?.toLowerCase().includes('authentication_required') || 
            roomError.message?.toLowerCase().includes('not authenticated')) {
          throw new Error('authentication_required');
        } else if (roomError.message.includes('room is full')) {
          toast({
            title: "Room is Full",
            description: "This room already has the maximum number of players",
            variant: "destructive"
          });
          throw roomError;
        } else if (roomError.message.includes('room not found')) {
          toast({
            title: "Room Not Found",
            description: "Invalid room code or room is no longer available",
            variant: "destructive"
          });
          throw roomError;
        } else {
          toast({
            title: "Error Joining Room",
            description: roomError.message || "Failed to join room",
            variant: "destructive"
          });
          throw roomError;
        }
      }

      if (!roomData || roomData.length === 0) {
        console.error('No room data returned from RPC');
        toast({
          title: "Room Not Found",
          description: "Invalid room code or room is no longer available",
          variant: "destructive"
        });
        return false;
      }

      const roomInfo = roomData[0];
      console.log('Successfully joined room:', roomInfo);
      
      // Set room state
      setRoom({
        id: roomInfo.room_id,
        room_code: roomInfo.room_code,
        time_control: roomInfo.time_control,
        status: 'waiting',
        created_by: 'unknown',
        room_name: roomInfo.room_name || undefined
      });

      // Fetch updated participants to determine player color and check if we should start game
      const { data: currentParticipants, error: participantsError } = await supabase
        .from('room_participants')
        .select('*')
        .eq('room_id', roomInfo.room_id);

      if (participantsError) {
        console.error('Error fetching participants:', participantsError);
      } else {
        console.log('Current participants after join:', currentParticipants);

        // If we're not in the participants list (e.g., RPC didn't add us), add ourselves now
        let updatedParticipants = currentParticipants || [];
        const myParticipation = updatedParticipants.find(p => p.user_id === user.id);
        if (!myParticipation) {
          const assignColor: 'white' | 'black' = updatedParticipants.length === 0 ? 'white' : 'black';
          console.warn('Not found as participant; inserting self with color:', assignColor);
          const { error: addSelfError } = await supabase
            .from('room_participants')
            .insert({
              room_id: roomInfo.room_id,
              user_id: user.id,
              player_color: assignColor,
              is_connected: true,
              joined_at: new Date().toISOString()
            });
          if (addSelfError) {
            console.error('Failed to add self as participant:', addSelfError);
          } else {
            const { data: refetched } = await supabase
              .from('room_participants')
              .select('*')
              .eq('room_id', roomInfo.room_id);
            updatedParticipants = refetched || updatedParticipants;
          }
        }

        setParticipants(updatedParticipants);

        const myNow = updatedParticipants.find(p => p.user_id === user.id);
        if (myNow && myNow.player_color) {
          setPlayerColor(myNow.player_color as 'white' | 'black');
          console.log('Player color assigned:', myNow.player_color);
        }

        // Enhanced auto-start game logic with better validation
        if (updatedParticipants && updatedParticipants.length === 2) {
          console.log('🎯 2 participants found, validating for game start');
          console.log('All participants:', updatedParticipants.map(p => ({ 
            userId: p.user_id, 
            color: p.player_color,
            isCurrentUser: p.user_id === user.id
          })));
          
          // Validate both players have colors assigned
          const whitePlayer = updatedParticipants.find(p => p.player_color === 'white');
          const blackPlayer = updatedParticipants.find(p => p.player_color === 'black');
          
          if (whitePlayer && blackPlayer) {
            // Check for existing game session first
            const { data: existingSession } = await supabase
              .from('game_sessions')
              .select('*')
              .eq('room_id', roomInfo.room_id)
              .eq('game_status', 'ongoing')
              .maybeSingle();

            if (!existingSession) {
              // Start new game with validation and retry logic
              console.log('✨ No existing session found, starting validated game...');
              setTimeout(async () => {
                try {
                  await startGame();
                  console.log('🎮 Game started successfully from join flow');
                } catch (error) {
                  console.error('❌ Failed to start game from join flow:', error);
                  // Retry once after a short delay
                  setTimeout(() => startGame(), 1000);
                }
              }, 300);
            } else {
              console.log('♻️ Game session already exists:', existingSession.id);
              const normalizedSession = {
                ...existingSession,
                game_state: existingSession.game_state ? normalizeGameState(existingSession.game_state) : null
              };
              setGameSession(normalizedSession);
            }
          } else {
            console.warn('⚠️ Color assignments incomplete - waiting for proper assignment');
            console.log('White player:', whitePlayer, 'Black player:', blackPlayer);
          }
        }
      }

      toast({
        title: "Joined Room",
        description: `Successfully joined room ${normalizedCode}`,
      });

      return true;
    }, 'room joining').catch((error) => {
      // Auth errors are already handled by useAuthResilience
      if (error.message !== 'authentication_required') {
        toast({
          title: "Error Joining Room",
          description: "Failed to join room. Please try again.",
          variant: "destructive"
        });
      }
      return false;
    });
  }, [toast, withAuthRetry]);

  const joinPersistentRoom = useCallback(async (persistentRoomCode: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to join a room",
          variant: "destructive"
        });
        return false;
      }

      // Find the persistent room
      const { data: persistentRoom, error: persistentRoomError } = await supabase
        .from('persistent_rooms')
        .select('*')
        .eq('room_code', persistentRoomCode)
        .single();

      if (persistentRoomError || !persistentRoom) {
        toast({
          title: "Room Not Found",
          description: "This persistent room doesn't exist or is no longer available",
          variant: "destructive"
        });
        return false;
      }

      // Create a temporary room for this game session using the persistent room's settings
      const tempRoomCode = `${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      
      // Create room using RPC to bypass RLS issues
      const { data: roomData, error: roomError } = await supabase
        .rpc('create_room_from_persistent', {
          _persistent_room_code: persistentRoomCode
        })
        .single();

      if (roomError) throw roomError;

      // Add user as first participant (white)
      const { error: participantError } = await supabase
        .from('room_participants')
        .upsert({
          room_id: roomData.id,
          user_id: user.id,
          player_color: 'white'
        }, {
          onConflict: 'room_id,user_id',
          ignoreDuplicates: false
        });

      if (participantError) throw participantError;

      // Update the persistent room's activity
      await supabase
        .from('persistent_rooms')
        .update({ 
          last_activity_at: new Date().toISOString(),
          total_games: persistentRoom.total_games + 1
        })
        .eq('id', persistentRoom.id);

      setRoom({
        ...roomData,
        status: 'waiting',
        created_by: user.id
      });
      setPlayerColor('white');
      
      toast({
        title: "Joined Room",
        description: `Entered ${persistentRoom.room_name}. Waiting for opponent...`,
      });
      
      return true;
    } catch (error: any) {
      console.error('Error joining persistent room:', error);
      toast({
        title: "Error Joining Room",
        description: error.message || "Failed to join room",
        variant: "destructive"
      });
      return false;
    }
  }, [toast]);

  const resignGame = useCallback(async () => {
    return withAuthRetry(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !gameSession || !playerColor) {
        throw new Error('authentication_required');
      }

      // Determine winner (opponent wins when current player resigns)
      const winner = playerColor === 'white' ? 'black' : 'white';
      
      // Update game session
      const { error } = await supabase
        .from('game_sessions')
        .update({
          game_status: 'resignation',
          winner: winner,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameSession.id);

      if (error) throw error;

      // Update ELO ratings using secure function with game session ID
      if (gameSession.white_player_id && gameSession.black_player_id) {
        const { error: eloError } = await supabase.rpc('update_player_elos_secure', {
          _game_session_id: gameSession.id,
          _winner: winner,
          _game_status: 'resignation'
        });
        
        if (eloError) {
          console.error('Failed to update ELO ratings:', eloError);
          // Don't throw error here as resignation is more important than ELO update
        } else {
          // Trigger currency refresh to update UI after database changes
          window.dispatchEvent(new CustomEvent('currency-updated'));
        }
      }

      toast({
        title: "Game Resigned",
        description: `You have resigned the game`,
        variant: "destructive"
      });

      return true;
    }, 'game resignation').catch((error) => {
      if (error.message !== 'authentication_required') {
        toast({
          title: "Resignation Failed",
          description: "Could not resign the game. Please try again.",
          variant: "destructive"
        });
      }
      return false;
    });
  }, [withAuthRetry, gameSession, playerColor, toast]);

  const offerDraw = useCallback(async () => {
    return withAuthRetry(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !gameSession) {
        throw new Error('authentication_required');
      }

      // Update game session to draw
      const { error } = await supabase
        .from('game_sessions')
        .update({
          game_status: 'draw',
          winner: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameSession.id);

      if (error) throw error;

      // Update ELO ratings for draw using secure function
      if (gameSession.white_player_id && gameSession.black_player_id) {
        const { error: eloError } = await supabase.rpc('update_player_elos_secure', {
          _game_session_id: gameSession.id,
          _winner: 'draw',
          _game_status: 'draw'
        });
        
        if (eloError) {
          console.error('Failed to update ELO for draw:', eloError);
          // Don't throw error here as draw acceptance is more important than ELO update
        } else {
          // Trigger currency refresh to update UI after database changes
          window.dispatchEvent(new CustomEvent('currency-updated'));
        }
      }

      toast({
        title: "Draw Agreed",
        description: "The game has ended in a draw",
      });

      return true;
    }, 'draw offer').catch((error) => {
      if (error.message !== 'authentication_required') {
        toast({
          title: "Draw Failed",
          description: "Could not end the game in a draw. Please try again.",
          variant: "destructive"
        });
      }
      return false;
    });
  }, [withAuthRetry, gameSession, toast]);

  const startGame = useCallback(async () => {
    if (!room) {
      console.error('❌ Cannot start game - no room');
      return;
    }

    try {
      console.log('🚀 Starting game for room:', room.room_code, room.id);
      
      // Double-check for existing game session to prevent duplicates
      const { data: existingCheck } = await supabase
        .from('game_sessions')
        .select('id, game_status')
        .eq('room_id', room.id)
        .eq('game_status', 'ongoing')
        .maybeSingle();
        
      if (existingCheck) {
        console.log('⚠️ Game session already exists, fetching instead of creating:', existingCheck.id);
        const { data: fullSession } = await supabase
          .from('game_sessions')
          .select('*')
          .eq('id', existingCheck.id)
          .single();
        if (fullSession) {
          const normalizedSession = {
            ...fullSession,
            game_state: fullSession.game_state ? normalizeGameState(fullSession.game_state) : null
          };
          setGameSession(normalizedSession);
        }
        return;
      }

      const initialGameState = createInitialGameState();
      const { timeMs, incrementMs } = parseTimeControl(room.time_control);
      
      // Get current participants to ensure we have both players
      const { data: currentParticipants, error: participantsError } = await supabase
        .from('room_participants')
        .select('*')
        .eq('room_id', room.id);

      if (participantsError) throw participantsError;

      const whitePlayer = currentParticipants?.find(p => p.player_color === 'white');
      const blackPlayer = currentParticipants?.find(p => p.player_color === 'black');

      if (!whitePlayer || !blackPlayer) {
        console.error('❌ Missing players for game start:', { 
          whitePlayer: whitePlayer?.user_id || 'MISSING', 
          blackPlayer: blackPlayer?.user_id || 'MISSING',
          allParticipants: currentParticipants?.map(p => ({ id: p.user_id, color: p.player_color })) || []
        });
        throw new Error('Both players must be present to start the game');
      }

      console.log('✅ Creating game session with validated players:', {
        white: whitePlayer.user_id,
        black: blackPlayer.user_id,
        room: room.room_code,
        participantCount: currentParticipants?.length
      });
      
      const { data: sessionData, error } = await supabase
        .from('game_sessions')
        .insert({
          room_id: room.id,
          game_state: initialGameState as any, // Cast to any for JSON storage
          white_player_id: whitePlayer.user_id,
          black_player_id: blackPlayer.user_id,
          white_time_ms: timeMs,
          black_time_ms: timeMs,
          increment_ms: incrementMs,
          last_tick_at: new Date().toISOString(),
          clocks_paused: false
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Database error creating game session:', error);
        throw error;
      }

      console.log('🎮 Game session created successfully:', sessionData.id);
      const normalizedSession = {
        ...sessionData,
        game_state: sessionData.game_state ? normalizeGameState(sessionData.game_state) : null
      };
      setGameSession(normalizedSession);

      // Update room status to active now that game has started
      await supabase.rpc('fix_room_status');
      
      toast({
        title: "Game Started! 🎯",
        description: "Both players connected. Good luck!",
      });

    } catch (error: any) {
      console.error('❌ Error in startGame:', error);
      toast({
        title: "Error Starting Game",
        description: error.message || "Failed to start game",
        variant: "destructive"
      });
    }
  }, [room, toast]);

  const makeGameMove = useCallback(async (from: Position, to: Position) => {
    if (!gameSession || !playerColor) return false;

    // Check if it's player's turn
    if (gameSession.current_turn !== playerColor) {
      toast({
        title: "Not Your Turn",
        description: "Wait for your opponent to make their move",
        variant: "destructive"
      });
      return false;
    }

    try {
      const currentState = gameSession.game_state;
      
      // Validate and make the move
      const isValidMove = validateMove(currentState, from, to);
      if (!isValidMove) {
        toast({
          title: "Invalid Move",
          description: "That move is not allowed",
          variant: "destructive"
        });
        return false;
      }

      const newGameState = makeMove(currentState, from, to);
      const nextTurn = gameSession.current_turn === 'white' ? 'black' : 'white';
      
      // Check for game ending conditions
      const gameResult = getGameStatus(newGameState);
      const isGameComplete = gameResult.status === 'checkmate' || gameResult.status === 'stalemate' || gameResult.status === 'draw';
      
      // Calculate updated clock times
      const now = new Date();
      const lastTickAt = new Date(gameSession.last_tick_at || now);
      const elapsed = now.getTime() - lastTickAt.getTime();
      
      let whiteTimeMs = gameSession.white_time_ms || 0;
      let blackTimeMs = gameSession.black_time_ms || 0;
      
      // Subtract elapsed time from current player
      if (gameSession.current_turn === 'white') {
        whiteTimeMs = Math.max(0, whiteTimeMs - elapsed);
        // Add increment to player who just moved
        whiteTimeMs += gameSession.increment_ms || 0;
      } else {
        blackTimeMs = Math.max(0, blackTimeMs - elapsed);
        // Add increment to player who just moved
        blackTimeMs += gameSession.increment_ms || 0;
      }
      
      // Check for timeout
      const isTimeout = whiteTimeMs <= 0 || blackTimeMs <= 0;
      let finalGameStatus = 'ongoing';
      let winner = null;
      
      if (isTimeout) {
        finalGameStatus = 'timeout';
        winner = whiteTimeMs <= 0 ? 'black' : 'white';
      } else if (isGameComplete) {
        finalGameStatus = gameResult.status;
        winner = gameResult.status === 'checkmate' ? gameResult.winner : null;
      }

      // Update game session in database
      const { error } = await supabase
        .from('game_sessions')
        .update({
          game_state: newGameState as any, // Cast to any for JSON storage
          current_turn: nextTurn,
          last_move: { from, to } as any,
          move_count: gameSession.move_count + 1,
          white_time_ms: whiteTimeMs,
          black_time_ms: blackTimeMs,
          last_tick_at: now.toISOString(),
          game_status: finalGameStatus,
          winner: winner
        })
        .eq('id', gameSession.id);

      if (error) throw error;

      // Handle game completion scenarios
      if (isTimeout) {
        const timeoutWinner = whiteTimeMs <= 0 ? 'black' : 'white';
        
        // Update ELO ratings and award currency for timeout
        if (gameSession.white_player_id && gameSession.black_player_id) {
          const { error: eloError } = await supabase.rpc('update_player_elos_secure', {
            _game_session_id: gameSession.id
          });
          
          if (eloError) {
            console.error('Failed to update ELO ratings for timeout:', eloError);
          } else {
            // Trigger currency refresh to update UI after database changes
            window.dispatchEvent(new CustomEvent('currency-updated'));
          }
        }
        
        toast({
          title: "Time's Up!",
          description: `${timeoutWinner === 'white' ? 'White' : 'Black'} wins on time!`,
          variant: "default"
        });
      } else if (isGameComplete) {
        // Handle checkmate, stalemate, or draw
        if (gameSession.white_player_id && gameSession.black_player_id) {
          const { error: eloError } = await supabase.rpc('update_player_elos_secure', {
            _game_session_id: gameSession.id
          });
          
          if (eloError) {
            console.error('Failed to update ELO ratings for game completion:', eloError);
          } else {
            // Trigger currency refresh to update UI after database changes
            window.dispatchEvent(new CustomEvent('currency-updated'));
          }
        }
        
        // Show appropriate message
        if (gameResult.status === 'checkmate') {
          toast({
            title: "Checkmate!",
            description: `${gameResult.winner === 'white' ? 'White' : 'Black'} wins!`,
            variant: "default"
          });
        } else if (gameResult.status === 'stalemate') {
          toast({
            title: "Stalemate!",
            description: "The game ends in a draw.",
            variant: "default"
          });
        } else if (gameResult.status === 'draw') {
          toast({
            title: "Draw!",
            description: `Game drawn by ${gameResult.reason || 'agreement'}.`,
            variant: "default"
          });
        }
      }

      return true;
    } catch (error: any) {
      console.error('Error making move:', error);
      toast({
        title: "Error Making Move",
        description: error.message || "Failed to make move",
        variant: "destructive"
      });
      return false;
    }
  }, [gameSession, playerColor, toast]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!room) return;

    console.log(`Setting up enhanced subscriptions for room ${room.room_code} (${room.id})`);

    const roomChannel = supabase
      .channel(`room-${room.room_code}-enhanced`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'room_participants',
        filter: `room_id=eq.${room.id}`
      }, (payload) => {
        if (!isMountedRef.current) return;
        console.log('📡 Participants real-time change:', payload.eventType, payload.new || payload.old);
        // Add small delay to ensure database consistency
        setTimeout(() => {
          if (!isMountedRef.current) return;
          fetchParticipants();
        }, 100);
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'game_sessions',
        filter: `room_id=eq.${room.id}`
      }, (payload) => {
        if (!isMountedRef.current) return;
        console.log('🎮 Game session real-time change:', payload.eventType, payload.new || payload.old);
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          console.log('Setting game session from real-time:', payload.new);
          const normalizedSession = {
            ...payload.new,
            game_state: payload.new.game_state ? normalizeGameState(payload.new.game_state) : null
          };
          setGameSession(normalizedSession as any);
          // Ensure participants are up to date when game session changes
          setTimeout(() => {
            if (!isMountedRef.current) return;
            fetchParticipants();
          }, 50);
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'rooms',
        filter: `id=eq.${room.id}`
      }, (payload) => {
        if (!isMountedRef.current) return;
        console.log('🏠 Room status real-time change:', payload);
        if (payload.new) {
          setRoom(payload.new as any);
        }
      })
      .subscribe((status) => {
        if (!isMountedRef.current) return status;
        console.log('🔌 Subscription status:', status);
        setIsConnected(status === 'SUBSCRIBED');
        
        // Fetch initial data when subscribed
        if (status === 'SUBSCRIBED') {
          setTimeout(() => {
            if (!isMountedRef.current) return;
            fetchParticipants();
          }, 200);
        }
        return status;
      });

    setChannel(roomChannel);

    // Fetch initial data immediately
    fetchParticipants();

    return () => {
      console.log('🧹 Cleaning up enhanced subscriptions');
      roomChannel.unsubscribe();
      if (!isMountedRef.current) return;
      setIsConnected(false);
    };
  }, [room]);

  const fetchParticipants = useCallback(async () => {
    if (!room) return;

    console.log(`Fetching participants for room ${room.id}`);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !isMountedRef.current) return;

    const { data, error } = await supabase
      .from('room_participants')
      .select('*')
      .eq('room_id', room.id);

    if (!isMountedRef.current) return;

    if (error) {
      console.error('Error fetching participants:', error);
      return;
    }

    if (data) {
      console.log('Participants found:', data.length, 'participants');
      setParticipants(data as any);
      
      // Determine and set current user's player color
      const currentUserParticipant = data.find(p => p.user_id === user.id);
      if (currentUserParticipant && currentUserParticipant.player_color) {
        setPlayerColor(currentUserParticipant.player_color as 'white' | 'black');
        console.log('Player color set to:', currentUserParticipant.player_color);
      }
      
      // Check for existing game session first
      const { data: existingSession, error: sessionError } = await supabase
        .from('game_sessions')
        .select('*')
        .eq('room_id', room.id)
        .eq('game_status', 'ongoing')
        .maybeSingle();

      if (!isMountedRef.current) return;

      if (sessionError && sessionError.code !== 'PGRST116') {
        console.error('Error fetching game session:', sessionError);
        return;
      }

      if (existingSession) {
        console.log('Found existing game session:', existingSession);
        setGameSession(existingSession);
        
        // Update room status to active if it's not already
        if (room.status !== 'active') {
          console.log('Updating room status to active via RPC');
          await supabase.rpc('fix_room_status');
        }
      } else if (data.length === 2) {
        // Enhanced 2-player game start logic with better validation
        console.log('🎯 2 participants present in fetchParticipants, validating for auto-start...');
        
        const whitePlayer = data.find(p => p.player_color === 'white');
        const blackPlayer = data.find(p => p.player_color === 'black');
        
        console.log('🎨 Color assignments validated:', { 
          whitePlayer: whitePlayer ? whitePlayer.user_id : 'MISSING',
          blackPlayer: blackPlayer ? blackPlayer.user_id : 'MISSING',
          allParticipants: data.map(p => ({ userId: p.user_id, color: p.player_color }))
        });
        
        if (whitePlayer && blackPlayer) {
          console.log('✅ Both colors assigned correctly - initiating game start sequence');
          
          // Double-check no race condition with existing game session
          const { data: doubleCheckSession } = await supabase
            .from('game_sessions')
            .select('*')
            .eq('room_id', room.id)
            .eq('game_status', 'ongoing')
            .maybeSingle();
            
          if (!isMountedRef.current) return;

          if (!doubleCheckSession && !gameSession) {
            console.log('🚀 Confirmed no existing session - starting new game');
            // Slightly longer delay for fetchParticipants auto-start to avoid race conditions
            setTimeout(() => {
              if (!isMountedRef.current) return;
              if (!gameSession) { // Double-check gameSession state hasn't changed
                console.log('🎮 Executing auto-start from fetchParticipants');
                startGame().then(() => {
                  console.log('✨ Auto-start completed successfully');
                }).catch((error) => {
                  console.error('❌ Auto-start failed:', error);
                });
              } else {
                console.log('🔄 Game session already exists, skipping auto-start');
              }
            }, 500);
          } else {
            console.log('♻️ Game session already exists or in state, skipping auto-start');
          }
        } else {
          console.warn('⚠️ Color assignment still incomplete in fetchParticipants:', { whitePlayer, blackPlayer });
          // Try to fix color assignments if missing
          if (!whitePlayer || !blackPlayer) {
            console.log('🔧 Attempting to fix color assignments...');
            // Could add logic here to reassign colors if needed
          }
        }
      }
    }
  }, [room, gameSession, startGame]);

  const leaveRoom = useCallback(async () => {
    if (!room) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('room_participants')
        .delete()
        .eq('room_id', room.id)
        .eq('user_id', user.id);

      if (!isMountedRef.current) return;

      // Clean up state
      setRoom(null);
      setGameSession(null);
      setParticipants([]);
      setPlayerColor(null);
      
      if (channel) {
        channel.unsubscribe();
        setChannel(null);
      }
      
      setIsConnected(false);
    } catch (error) {
      console.error('Error leaving room:', error);
    }
  }, [room, channel]);

  return {
    room,
    gameSession,
    participants,
    playerColor,
    isConnected,
    createRoom,
    joinRoom,
    joinPersistentRoom,
    startGame,
    makeGameMove,
    resignGame,
    offerDraw,
    leaveRoom
  };
}